import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAgentOptions } from '@/features/agent-targets/use-agent-targets';
import { useBranchesList } from '@/features/branches/use-branches';
import * as citiesApi from '@/features/cities/cities.api';
import { useCitiesList } from '@/features/cities/use-cities';
import * as countriesApi from '@/features/countries/countries.api';
import * as customersApi from '@/features/customers/customers.api';
import { useMealPlansList } from '@/features/meal-plans/use-meal-plans';
import { useQueryMediumsList } from '@/features/query-mediums/use-query-mediums';
import { useQuerySourcesList } from '@/features/query-sources/use-query-sources';
import { useQueryStatusesList } from '@/features/query-statuses/use-query-statuses';
import { useQuerySubStatusesList } from '@/features/query-sub-statuses/use-query-sub-statuses';
import { useQueryTypesList } from '@/features/query-types/use-query-types';

/**
 * Cross-feature option hooks scoped to the Leads UI.
 *
 * Each master master module already exposes a `useXxxList` hook backed by the
 * standard list endpoint. The Leads form / filters need plain `{value,label}`
 * arrays - these helpers wrap the list hooks with `pageSize=200, isActive=true`
 * (every master we touch has ≤100 rows) and project the rows.
 *
 * Keeping these in the Leads feature rather than each master is intentional:
 * the master modules stay focused on their own CRUD and we don't need to
 * touch every one to add an `useXxxOptions` hook.
 */

const LIST_QUERY_BASE = {
  page: 1 as const,
  pageSize: 200 as const,
  sortDir: 'asc' as const,
  sortBy: 'sortOrder',
  isActive: true,
};

const STALE_OPTIONS = 5 * 60_000;

export const useQueryTypeOptions = () => {
  const q = useQueryTypesList(LIST_QUERY_BASE);
  const options = useMemo(
    () => (q.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

export const useQuerySourceOptions = () => {
  const q = useQuerySourcesList(LIST_QUERY_BASE);
  const options = useMemo(
    () => (q.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

export const useQueryMediumOptions = () => {
  const q = useQueryMediumsList(LIST_QUERY_BASE);
  const options = useMemo(
    () => (q.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

export const useQueryStatusOptionsWithBucket = () => {
  const q = useQueryStatusesList(LIST_QUERY_BASE);
  const data = useMemo(() => q.data?.data ?? [], [q.data]);
  const options = useMemo(() => data.map((row) => ({ value: row.id, label: row.name })), [data]);
  return { data, options, isLoading: q.isLoading };
};

/**
 * Sub-statuses belonging to the given parent status. Used by the lead
 * form's edit-mode Status / Sub-Status cascade. When `parentStatusId`
 * is null/undefined the query is disabled and the returned options
 * array is empty - the UI hides the sub-status picker in that case.
 */
export const useQuerySubStatusOptionsForParent = (parentStatusId: string | null | undefined) => {
  const q = useQuerySubStatusesList({
    ...LIST_QUERY_BASE,
    queryStatusId: parentStatusId ?? undefined,
  });
  const data = useMemo(
    () => (parentStatusId ? (q.data?.data ?? []) : []),
    [q.data, parentStatusId],
  );
  const options = useMemo(() => data.map((row) => ({ value: row.id, label: row.name })), [data]);
  return { data, options, isLoading: parentStatusId ? q.isLoading : false };
};

/**
 * Active meal plans for the lead form's Meal-option picker (ADR-0047).
 * Replaces the hardcoded `MealOption` enum dropdown - admins now manage
 * the list through Masters -> Meal Plans. The label is the master's
 * `name` (sortOrder asc); `value` is the master row's `id` which the
 * lead form sends as `mealPlanId`.
 */
export const useMealPlanOptions = () => {
  const q = useMealPlansList(LIST_QUERY_BASE);
  const options = useMemo(
    () => (q.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

export const useBranchOptions = () => {
  const q = useBranchesList(LIST_QUERY_BASE);
  const options = useMemo(
    () => (q.data?.data ?? []).map((row) => ({ value: row.id, label: row.name })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

/**
 * Active cities for the lead form's Departure / Destination pickers.
 * Server-driven from the City master with `isActive: true`, `pageSize: 200`.
 * The label includes the country in parentheses so duplicated city names
 * (e.g. "Hyderabad, IN" vs "Hyderabad, PK") are distinguishable.
 */
export const useCityOptionsForLeads = () => {
  const q = useCitiesList(LIST_QUERY_BASE);
  const options = useMemo(
    () =>
      (q.data?.data ?? []).map((row) => ({
        value: row.id,
        label: row.country?.isoCode
          ? `${row.name} (${row.country.isoCode.toUpperCase()})`
          : row.name,
      })),
    [q.data],
  );
  return { options, isLoading: q.isLoading };
};

/**
 * "Marketing Destinations" for the lead form picker (ADR-0041).
 *
 * Lists every active destination row from the Destinations master.
 * Per the 2026-06-10 product update we intentionally do NOT filter by
 * the underlying country's status - marketing-owned destinations can
 * be region-only ("Southeast Asia Beach Holidays") with no country at
 * all, and the operator should always see the canonical list as
 * maintained by the marketing team.
 */
export const useDestinationOptionsForLeads = (opts?: { enabled?: boolean }) => {
  const q = useQuery({
    queryKey: ['destinations', 'leads-picker'],
    queryFn: async () => {
      const { listDestinations } = await import('@/features/destinations/destinations.api');
      // 200 is the API's page cap and the master has outgrown it, so read every
      // page - otherwise the tail of the list is missing from every picker.
      const query = { pageSize: 200, sortDir: 'asc', isActive: true } as const;
      const first = await listDestinations({ ...query, page: 1 });
      const rest = await Promise.all(
        Array.from({ length: first.pagination.totalPages - 1 }, (_, i) =>
          listDestinations({ ...query, page: i + 2 }),
        ),
      );
      return { ...first, data: [first.data, ...rest.map((r) => r.data)].flat() };
    },
    staleTime: STALE_OPTIONS,
    enabled: opts?.enabled ?? true,
  });
  const options = useMemo(
    () =>
      (q.data?.data ?? []).map((d) => {
        const where = [d.city?.name, d.state?.name, d.country?.name].filter(Boolean).join(', ');
        // Suffix priority: city-state-country chain > region label >
        // bare name. Either region or country is always present
        // (schema-enforced), so a destination is never label-less.
        const suffix = where || d.region?.name || '';
        return {
          value: d.id,
          label: suffix ? `${d.name} - ${suffix}` : d.name,
        };
      }),
    [q.data],
  );
  return { options, isLoading: q.isLoading, isError: q.isError };
};

/**
 * Searchable destination picker for user sales-coverage (admin Users form).
 * Filters to active destinations under active countries and supports
 * server-side name search when the operator types 2+ characters.
 */
export const useDestinationPickerSearch = (opts: { search: string; enabled?: boolean }) => {
  const trimmed = opts.search.trim();
  const q = useQuery({
    queryKey: ['destinations', 'user-coverage-picker', trimmed],
    queryFn: async () => {
      const { listDestinations } = await import('@/features/destinations/destinations.api');
      return listDestinations({
        page: 1,
        pageSize: 200,
        sortBy: 'sortOrder',
        sortDir: 'asc',
        isActive: true,
        onlyActiveCountries: true,
        ...(trimmed.length >= 2 ? { search: trimmed } : {}),
      });
    },
    staleTime: STALE_OPTIONS,
    enabled: opts.enabled ?? true,
  });
  const options = useMemo(
    () =>
      (q.data?.data ?? []).map((d) => {
        const where = [d.city?.name, d.state?.name, d.country?.name].filter(Boolean).join(', ');
        const suffix = where || d.region?.name || '';
        return {
          value: d.id,
          label: suffix ? `${d.name} - ${suffix}` : d.name,
        };
      }),
    [q.data],
  );
  return {
    options,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
  };
};

/** Active agents (sales staff) - re-exports the existing user-options hook. */
export const useAgentOptionsForLeads = () => {
  const q = useAgentOptions();
  const data = useMemo(() => q.data ?? [], [q.data]);
  const options = useMemo(
    () => data.map((row) => ({ value: row.id, label: row.fullName })),
    [data],
  );
  /** Quick lookup from agent UUID → their branchId (null when unassigned). */
  const branchByAgentId = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of data) map.set(row.id, row.branchId);
    return map;
  }, [data]);
  return { options, branchByAgentId, isLoading: q.isLoading };
};

/**
 * 2026-07-08. Assignable-agents list KEEPING each row's role so a caller
 * can render a role-grouped filter dropdown (SALES_LEAD → their agents,
 * ADMIN → every lead + agent). Scoping is server-side: an ADMIN gets
 * every active SALES_LEAD + SALES_AGENT, a SALES_LEAD gets self + direct
 * reports, a SALES_AGENT gets self only. The dedicated hook lives here
 * so the /users/assignable-agents call is deduped with the existing
 * lead-form consumer via React Query's cache key.
 */
export const useAgentOptionsForLeadsWithRole = () => {
  const q = useAgentOptions();
  const data = useMemo(() => q.data ?? [], [q.data]);
  return { data, isLoading: q.isLoading };
};

// ----- Customer picker (search-by-name) -----

/**
 * Server-side customer search. The customer master exposes a full list
 * endpoint with `search` (trigram fuzzy across name / email / mobile); we
 * page-size cap to 20 since the picker is a combobox.
 */
export const useCustomerSearch = (term: string, kind?: 'INDIVIDUAL' | 'CORPORATE') => {
  const trimmed = term.trim();
  const enabled = trimmed.length >= 2;
  const q = useQuery({
    queryKey: ['customers', 'leads-picker', trimmed, kind ?? 'ALL'],
    queryFn: () =>
      customersApi.listCustomers({
        page: 1,
        pageSize: 20,
        sortDir: 'asc',
        sortBy: 'displayName',
        q: trimmed,
        ...(kind ? { kind } : {}),
      }),
    enabled,
    staleTime: STALE_OPTIONS,
  });
  return {
    customers: q.data?.data ?? [],
    isLoading: q.isFetching && enabled,
    enabled,
  };
};

// ============================================================================
// Departure-city picker (lead-category-aware, server-side search)
// ============================================================================

/// ISO-3166-1 alpha-2 of the home country - hardcoded because the
/// product is shipped to a single Indian travel agency today. Promote to
/// AppConfig when we ship to a second tenant.
const HOME_COUNTRY_ISO_CODE = 'in';

/**
 * Resolve the home country's UUID (India) by ISO code. Used to drive
 * the lead form's domestic/international split on the departure-city
 * picker - the dropdown filters by `countryId` for Domestic, and by
 * `excludeCountryId` for International. Cached forever (the row is
 * effectively immutable in our DB).
 *
 * Returns null until the lookup resolves; callers must guard.
 */
export const useHomeCountryId = (): string | null => {
  const q = useQuery({
    queryKey: ['countries', 'home-country-id', HOME_COUNTRY_ISO_CODE],
    queryFn: async () => {
      // `search` does a case-insensitive `contains` on isoCode (and a
      // few other fields), so "in" matches "IN", "IND", "FIN", "CHN"...
      // Page the first 25 results and filter client-side to the exact
      // alpha-2 we want; cheap because alpha-2 is unique.
      const res = await countriesApi.listCountries({
        page: 1,
        pageSize: 25,
        sortDir: 'asc',
        search: HOME_COUNTRY_ISO_CODE,
      });
      const exact = res.data.find((c) => c.isoCode.toLowerCase() === HOME_COUNTRY_ISO_CODE);
      return exact?.id ?? null;
    },
    staleTime: Infinity,
  });
  return q.data ?? null;
};

/**
 * Server-side city search for the lead form's Departure-city picker.
 *
 * - `category = 'DOMESTIC'`  → list cities WHERE `countryId = India`
 * - `category = 'INTERNATIONAL'` → list cities WHERE `countryId != India`
 * - `category = undefined`  → no country filter (the operator hasn't
 *   picked the lead category yet, but they can still browse all cities)
 *
 * `term` is the live search box value. Always sends with `isActive=true`
 * so disabled City master rows never appear in the picker. Limited to
 * 50 results - the picker is a combobox, not a table.
 */
/**
 * Server-side city search with infinite-scroll / load-more pagination.
 *
 * Backs both DepartureCityPicker and DestinationCityPicker on the lead
 * form. The query is intentionally simple - no DOMESTIC / INTERNATIONAL
 * country filter, so departure and destination behave the same way:
 * the operator searches the full active catalogue, picks any city.
 *
 * The query enforces:
 *
 *  - `isActive: true`         the city itself is active
 *  - `countryIsActive: true`  the parent country is also active
 *
 * so deactivating a country immediately hides every city under it
 * from intake, without depending on the bulk-activate post-seed
 * script having been re-run.
 *
 * Pagination is keyed on the trimmed search term so typing a single
 * character resets to page 1. Empty term returns the first 20 active
 * cities sorted by name; `fetchNextPage` advances one server page
 * (20 rows) at a time.
 *
 * Full-text search: the server `search` param with `searchMode: 'prefix'`
 * matches city name / IATA code / custom city code from the start of the
 * string (case-insensitive). State-name matches are excluded.
 */
const CITY_PICKER_PAGE_SIZE = 20;

export const useCitySearchForLeads = (params: { term: string; countryId?: string | null }) => {
  const { term, countryId } = params;
  const trimmed = term.trim();
  const q = useInfiniteQuery({
    queryKey: ['cities', 'leads-picker', trimmed, countryId ?? ''],
    initialPageParam: 1 as number,
    queryFn: ({ pageParam }) =>
      citiesApi.listCities({
        page: pageParam,
        pageSize: CITY_PICKER_PAGE_SIZE,
        sortDir: 'asc',
        sortBy: 'name',
        isActive: true,
        countryIsActive: true,
        // 2026-06-24: callers can pin the picker to one country
        // (the lead form's Departure picker passes the home-country
        // id so departure can only be an Indian city).
        ...(countryId ? { countryId } : {}),
        // Send the term as soon as anything's typed - the server
        // falls through to "no search" for an empty/whitespace-only
        // term anyway. Lets the operator narrow with a single char.
        ...(trimmed.length > 0 ? { search: trimmed } : {}),
        // 2026-06-27. Lead-form pickers use PREFIX matching so
        // typing "del" returns Delhi / Dehradun only - matches
        // operator typing rhythm. The Cities admin grid + other
        // pickers keep substring (default).
        searchMode: 'prefix' as const,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  const cities = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data?.pages]);
  return {
    cities,
    isLoading: q.isFetching && !q.isFetchingNextPage,
    isLoadingMore: q.isFetchingNextPage,
    hasMore: q.hasNextPage ?? false,
    loadMore: () => {
      if (q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
    },
    isReady: true,
  };
};
